import { toast } from 'react-toastify';
import md5 from 'md5';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import header from '../../../assets/icm-header.jpg';
import {
  getBase64PDFImage,
  scaleStyles,
  baseStyles,
  getPrefix,
} from '../../../util/utils';
import {
  getPdfBase64,
  enviarArquivo,
  normalizeWhatsappPhone,
} from '../../../services/pdfWhatsapp';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

function mapListSection(
  title,
  array,
  keyField = 'DESCRICAO',
  pageBreakBefore = false
) {
  if (!Array.isArray(array) || array.length === 0) return [];
  const items = array
    .map(item => item && item[keyField])
    .filter(Boolean)
    .map(texto => ({
      text: `- ${texto}`,
      margin: [5, 0, 0, 2],
      style: 'normal',
    }));
  if (items.length === 0) return [];
  return [
    {
      text: title,
      style: 'subheader',
      margin: [0, 5, 0, 2],
      ...(pageBreakBefore ? { pageBreak: 'before' } : {}),
    },
    ...items,
    { text: '', margin: [0, 2, 0, 2] },
  ];
}

function mapSingleField(label, value, unit = '', precision = 2) {
  if (value === null || value === undefined || value === '') return null;
  let fieldValue;
  if (Number.isNaN(Number(value))) {
    fieldValue = value;
  } else {
    fieldValue = Number(value).toFixed(precision);
  }
  const suffix = unit ? ` ${unit}` : '';
  return {
    text: `- ${label}: ${fieldValue}${suffix}`,
    margin: [5, 0, 0, 2],
    style: 'normal',
  };
}

function mapMixedSection(
  title,
  numbers = [],
  list = [],
  keyField = 'DESCRICAO',
  pageBreakBefore = false
) {
  const validNumbers = (numbers || []).filter(
    n => n && n.value !== null && n.value !== undefined && n.value !== ''
  );
  const validList = (list || []).filter(item => item && item[keyField]);

  const content = [];
  if (validNumbers.length > 0 || validList.length > 0) {
    content.push({
      text: title,
      style: 'subheader',
      margin: [0, 5, 0, 2],
      ...(pageBreakBefore ? { pageBreak: 'before' } : {}),
    });
    validNumbers.forEach(n => {
      content.push({
        text: `- ${n.label}: ${n.value}${n.unit ? ` ${n.unit}` : ''}`,
        margin: [5, 0, 0, 2],
        style: 'normal',
      });
    });
    validList.forEach(item => {
      content.push({
        text: `- ${item[keyField]}`,
        margin: [5, 0, 0, 2],
        style: 'normal',
      });
    });
    content.push({ text: '', margin: [0, 2, 0, 2] });
  }
  return content;
}

export default async function generateDopplerPDF(
  eco,
  tipo = 'download',
  fontScale = 0.9,
  pageBreaks = {}
) {
  const {
    pageBreakA = false,
    pageBreakB = false,
    pageBreakC = false,
    cpfPaciente = false,
  } = pageBreaks || {};
  const scaledStyles = scaleStyles(baseStyles, fontScale);
  const styles = {
    ...scaledStyles,
    header: { fontSize: 16, bold: true },
    subheader: { fontSize: 11, bold: true, margin: [0, 5, 0, 2] },
    normal: { fontSize: 10 },
    pre: { fontSize: 10, italics: true },
  };

  const headerImage =
    tipo === 'imprimirTimbrado' || tipo === 'envioWpp'
      ? await getBase64PDFImage(header)
      : null;

  const dadosAntropometricos = [
    eco?.PESOPAC ? `Peso: ${eco.PESOPAC} Kg` : null,
    eco?.ALTURAPAC ? `Altura: ${eco.ALTURAPAC} m` : null,
    eco?.FC ? `FC: ${eco.FC} bpm` : null,
  ].filter(Boolean);

  let content = [
    {
      text: 'ECOCARDIOGRAMA DOPPLER',
      style: 'title',
      alignment: 'center',
      margin: [0, 0, 0, 5],
    },
    {
      table: {
        widths: ['*'],
        body: [
          [
            {
              stack: [
                {
                  columns: [
                    {
                      text: `Nome do Paciente: ${eco?.CODPAC || ''} - ${eco?.NOMEPAC || ''
                        }${cpfPaciente && eco?.CPFPAC ? ` - CPF: ${eco.CPFPAC}` : ''
                        }`,
                      width: '100%',
                      bold: true,
                    },
                  ],
                },
                {
                  columns: [
                    {
                      text: `Data Nasc: ${eco?.dataNascFmt || eco?.dataNasc || ''
                        }`,
                      width: '50%',
                    },
                    {
                      text: `Sexo: ${eco?.Sexo || eco?.SEXOPAC || ''}`,
                      width: '20%',
                    },
                    {
                      text: `Idade: ${eco?.idadePAC ?? ''} anos`,
                      width: '30%',
                    },
                  ],
                },
                ...(dadosAntropometricos.length > 0
                  ? [
                    {
                      columns: [
                        {
                          text: dadosAntropometricos.join('    '),
                          width: '100%',
                        },
                      ],
                    },
                  ]
                  : []),
                {
                  columns: [
                    {
                      text: `Convênio: ${eco?.SIGLACONV || ''}`,
                      width: '50%',
                    },
                    {
                      text: `Código: ${eco?.CODECO || ''}`,
                      width: '20%',
                    },
                    {
                      text: `Data Laudo: ${eco?.dataEcoFmt || ''}`,
                      width: '30%',
                    },
                  ],
                },
                {
                  columns: [
                    {
                      text: `Médico que Solicitou: ${getPrefix(
                        eco?.CODSOLICITANTE
                      )}${eco?.Solicitou || ''}`,
                      width: '100%',
                    },
                  ],
                },
              ],
              margin: [10, 2, 10, 2],
            },
          ],
        ],
      },
      layout: {
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => 'black',
        vLineColor: () => 'black',
      },
      style: 'content',
      margin: [0, 0, 0, 8],
    },
  ];

  // --- 01 - Observações iniciais ---
  if (Array.isArray(eco?.obs) && eco.obs.length > 0) {
    eco.obs.forEach(o => {
      content.push({
        text: `- ${o?.DESCRICAO || 'Descrição não encontrada'}`,
        margin: [5, 0, 0, 2],
        style: 'normal',
      });
    });
  }

  // --- 02 - Diâmetros e Função Sistólica do VE ---
  const veItems = [
    mapSingleField('Septo', eco?.SEPTO, '(cm)', 1),
    mapSingleField('PPVE', eco?.PPVE, '(cm)', 1),
    mapSingleField('Diâmetro Diastólico', eco?.DD, '(cm)', 1),
    mapSingleField('Diâmetro Sistólico', eco?.DS, '(cm)', 1),
    mapSingleField('Fração de Ejeção (Monoplanar)', eco?.fracaoEjecaoMono, ''),
    mapSingleField('Fração de Ejeção (Simpson)', eco?.FRAEJECBIPLA, '', 2),
    mapSingleField('Fração de Ejeção (3D)', eco?.FRAEJE3D, '', 2),
    eco?.massaVE != null
      ? {
        text: `- Massa do VE: ${Math.floor(Number(eco.massaVE))} (g/m²)`,
        margin: [5, 0, 0, 2],
        style: 'normal',
      }
      : null,
    mapSingleField('Relação Espessura/Volume', eco?.avaliaVSVE, '', 2),
  ].filter(Boolean);

  if (veItems.length > 0) {
    content.push(
      {
        text: 'Diâmetros e Função Sistólica do Ventrículo Esquerdo',
        style: 'subheader',
      },
      ...veItems
    );
  }

  // --- 03 a 16 --- listas e seções mistas
  content = content.concat(
    mapListSection('Achados Descritivos do VE', eco?.achados)
  );
  content = content.concat(
    mapListSection('Contração Segmentar', eco?.contraRegiao, 'CONTRASEG')
  );

  const diastItems = [
    mapSingleField('Índice de Performance', eco?.indicePM, '(Normal < 0.42)', 2),
    mapSingleField(
      'Estimativa do Débito Cardíaco',
      eco?.estimativaDC,
      '(l/min/m2) (2.4 a 4.2)'
    ),
  ].filter(Boolean);

  if (diastItems.length > 0 || (eco?.avDiast && eco.avDiast.length > 0)) {
    content = content.concat(
      mapMixedSection(
        'Avaliação da Função Diastólica do Ventrículo Esquerdo',
        [
          eco?.indicePM != null && !Number.isNaN(Number(eco.indicePM))
            ? {
              label: 'Índice de Performance',
              value: Number(eco.indicePM).toFixed(2),
              unit: '(Normal < 0.42)',
            }
            : null,
          eco?.estimativaDC != null && !Number.isNaN(Number(eco.estimativaDC))
            ? {
              label: 'Estimativa do Débito Cardíaco',
              value: eco.estimativaDC,
              unit: '(l/min/m2) (2.4 a 4.2)',
            }
            : null,
        ],
        eco?.avDiast
      )
    );
  }

  content = content.concat(
    mapListSection('Ventrículo Direito', eco?.ventriDir)
  );

  content = content.concat(
    mapMixedSection(
      'Átrio Esquerdo',
      [
        eco?.DAE != null
          ? {
            label: 'Diâmetro do Átrio Esquerdo',
            value: Number(eco.DAE).toFixed(1),
            unit: '(cm)',
          }
          : null,
        eco?.VOL_AE != null
          ? {
            label: 'Volume do AE',
            value: Number(eco.VOL_AE).toFixed(),
            unit: '(cc/m2) (até 34)',
          }
          : null,
      ],
      eco?.atrioEsq
    )
  );

  content = content.concat(mapListSection('Átrio Direito', eco?.atrioDir));

  // Seção Mitral (pageBreakA)
  const mitralSections = [
    ...mapListSection('Valva Mitral', eco?.valvaMitral),
    ...mapMixedSection(
      'Doppler Mitral',
      [
        eco?.AVM != null
          ? {
            label: 'Área Valva Mitral',
            value: Number(eco.AVM).toFixed(1),
            unit: `(cm2) ${eco?.valorAVM || ''}`.trim(),
          }
          : null,
        eco?.ORIFICIO_VM != null
          ? {
            label: 'Orifício Regurgitação Mitral (PISA)',
            value: Number(eco.ORIFICIO_VM).toFixed(2),
            unit: '(cm2)',
          }
          : null,
        eco?.GMVM != null
          ? { label: 'Gradiente Médio', value: eco.GMVM, unit: '(mmHg)' }
          : null,
        eco?.VOLUME_VM != null
          ? {
            label: 'Volume Regurgitação Mitral',
            value: eco.VOLUME_VM,
            unit: '(cc)',
          }
          : null,
      ],
      eco?.refVM
    ),
    ...mapMixedSection(
      'Prótese Mitral',
      [
        eco?.GMPM != null
          ? {
            label: 'Gradiente Médio da Prótese Mitral',
            value: eco.GMPM,
            unit: '(mmHg)',
          }
          : null,
      ],
      eco?.refluxoPM,
      'refluxoPM'
    ),
  ];

  if (pageBreakA && mitralSections.length > 0) {
    mitralSections[0] = { ...mitralSections[0], pageBreak: 'before' };
  }
  content = content.concat(mitralSections);

  // Seção Aórtica (pageBreakB)
  const aorticaSections = [
    ...mapMixedSection(
      'Valva Aórtica',
      [
        eco?.DAVAO != null
          ? {
            label: 'Diâmetro da Raiz Aórtica',
            value: Number(eco.DAVAO).toFixed(1),
            unit: '(cm)',
          }
          : null,
      ],
      eco?.valvaAO
    ),
    ...mapMixedSection(
      'Doppler Aórtico',
      [
        eco?.AREA_AO != null
          ? {
            label: 'Área Valva Aórtica',
            value: Number(eco.AREA_AO).toFixed(2),
            unit: `(cm2) ${eco?.valorAreaAVAO || ''}`.trim(),
          }
          : null,
        eco?.GMVAO != null
          ? {
            label: 'Gradiente Médio Valva Aórtica',
            value: eco.GMVAO,
            unit: '(mmHg)',
          }
          : null,
        eco?.GMVSVE != null
          ? {
            label: 'Gradiente Médio Via de Saída VE',
            value: eco.GMVSVE,
            unit: '(mmHg)',
          }
          : null,
      ],
      eco?.refVAO
    ),
    ...mapMixedSection(
      'Prótese Aórtica',
      [
        eco?.GMPAO != null
          ? {
            label: 'Gradiente Médio da Prótese Aórtica',
            value: eco.GMPAO,
            unit: '(mmHg)',
          }
          : null,
      ],
      eco?.refluxoPAO,
      'refluxoPAO'
    ),
  ];

  if (pageBreakB && aorticaSections.length > 0) {
    aorticaSections[0] = { ...aorticaSections[0], pageBreak: 'before' };
  }
  content = content.concat(aorticaSections);

  // Seção Tricúspide (pageBreakC)
  const tricuspideSections = [
    ...mapListSection('Valva Tricúspide', eco?.valvaTricu),
    ...mapMixedSection(
      'Doppler Tricúspide',
      [
        eco?.GMVT != null
          ? {
            label: 'Gradiente Médio da Valva Tricúspide',
            value: eco.GMVT,
            unit: '(mmHg)',
          }
          : null,
        eco?.estimativaPSVD != null
          ? {
            label: 'Estimativa Pressão Sistólica AP',
            value: eco.estimativaPSVD,
            unit: '(mmHg)',
          }
          : null,
      ],
      eco?.refVT
    ),
    ...mapMixedSection(
      'Prótese Tricúspide',
      [
        eco?.GMPT != null
          ? {
            label: 'Gradiente Médio da Prótese Tricúspide',
            value: eco.GMPT,
            unit: '(mmHg)',
          }
          : null,
      ],
      eco?.refluxoPT,
      'refluxoPT'
    ),
  ];

  if (pageBreakC && tricuspideSections.length > 0) {
    tricuspideSections[0] = { ...tricuspideSections[0], pageBreak: 'before' };
  }
  content = content.concat(tricuspideSections);

  content = content.concat(
    mapMixedSection(
      'Valva Pulmonar',
      [
        eco?.DAVP != null
          ? {
            label: 'Diâmetro do Anel Pulmonar',
            value: Number(eco.DAVP).toFixed(1),
            unit: '(cm)',
          }
          : null,
      ],
      eco?.valvaPulmo
    )
  );
  content = content.concat(
    mapMixedSection(
      'Doppler Pulmonar',
      [
        eco?.GMVP != null
          ? {
            label: 'Gradiente Médio da Valva Pulmonar',
            value: eco.GMVP,
            unit: '(mmHg)',
          }
          : null,
      ],
      eco?.refVP
    )
  );
  content = content.concat(
    mapMixedSection(
      'Prótese Pulmonar',
      [
        eco?.GMPP != null
          ? {
            label: 'Gradiente Médio da Prótese Pulmonar',
            value: eco.GMPP,
            unit: '(mmHg)',
          }
          : null,
      ],
      eco?.refluxoPP,
      'refluxoPP'
    )
  );

  content = content.concat(mapListSection('Pericárdio', eco?.pericardio));
  content = content.concat(
    mapListSection('Veia Cava Inferior', eco?.veiaCavaInf)
  );
  content = content.concat(mapListSection('Aorta', eco?.aorta));

  if (eco?.OBSERVACOES?.trim()) {
    content.push({ text: 'Observações', style: 'subheader' });
    content.push({
      text: `- ${eco.OBSERVACOES.trim()}`,
      style: 'pre',
      margin: [5, 0, 0, 2],
    });
  }

  content = content.concat(
    mapListSection('Conclusões', [
      ...(eco?.valvulopatia || []).map(v => ({ DESCRICAO: v.valvulopatia })),
      ...(eco?.protese || []).map(p => ({ DESCRICAO: p.protese })),
      ...(eco?.contraSeg || []).map(c => ({ DESCRICAO: c.contraSeg })),
      ...(eco?.conclusoes || []).map(c => ({ DESCRICAO: c.conclusoes })),
    ])
  );

  const assinaturaDigital = eco?.HASH || md5(JSON.stringify(eco || {}));

  // 1. Bloco das imagens (apenas se não for convênio 29 ou 804)
  if (eco?.CODCONV !== 29 && eco?.CODCONV !== 804) {
    content.push({
      table: {
        widths: ['*'],
        body: [
          [
            {
              text: `Imagens disponíveis no site: www.icm.com.br (Resultado de Exames)\nCódigo de verificação: ${eco?.CODPAC || ''}`,
              style: 'files',
              alignment: 'center',
            },
          ],
        ],
      },
      layout: {
        hLineColor: () => '#999',
        vLineColor: () => '#999',
        paddingLeft: () => 5,
        paddingRight: () => 5,
        paddingTop: () => 5,
        paddingBottom: () => 5,
      },
      margin: [0, 8, 0, 4],
    });
  }

  // 2. Bloco da assinatura eletrônica
  content.push({
    table: {
      widths: ['*'],
      body: [
        [
          {
            stack: [
              {
                text: 'Conferido e assinado eletronicamente por:',
                italics: true,
                alignment: 'center',
              },
              {
                text: `${getPrefix(eco?.CODREALIZOU)}${eco?.Realizou || ''}`,
                bold: true,
                alignment: 'center',
              },
              {
                text: `CRM: ${eco?.CRMMED || ''} RQE: ${eco?.RQE || ''}`,
                bold: true,
                style: 'small',
                alignment: 'center',
              },
              {
                text: `Assinatura Digital: ${assinaturaDigital}`,
                italics: true,
                style: 'smaller',
                alignment: 'center',
              },
              {
                text: [
                  { text: 'Emitido em: ', bold: true },
                  `${new Date().toLocaleString('pt-BR')}\n`,
                ],
                style: 'smaller',
                color: '#444',
                alignment: 'center',
              },
            ],
            style: 'content',
          },
        ],
      ],
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => '#999',
      vLineColor: () => '#999',
      paddingLeft: () => 5,
      paddingRight: () => 5,
      paddingTop: () => 5,
      paddingBottom: () => 5,
    },
    margin: [0, 4, 0, 0],
  });

  const docDefinition = {
    pageSize: 'A4',
    pageMargins: headerImage ? [40, 130, 40, 60] : [40, 60, 40, 60],
    footer: (currentPage, pageCount) => ({
      columns: [
        {
          text: `Página ${currentPage} de ${pageCount}`,
          alignment: 'center',
          style: 'footer',
        },
      ],
      margin: [0, 10, 0, 0],
    }),
    content: content.filter(Boolean),
    styles,
  };

  if (headerImage) {
    docDefinition.header = {
      image: headerImage,
      width: 500,
      alignment: 'center',
      margin: [0, 10, 0, 0],
    };
  }

  if (tipo === 'imprimirNormal' || tipo === 'imprimirTimbrado') {
    pdfMake.createPdf(docDefinition).open();
  } else if (tipo === 'envioWpp') {
    const celular = normalizeWhatsappPhone(eco?.CELULARPAC);
    if (!celular) {
      toast.warn('Número de celular não encontrado!');
      return undefined;
    }

    try {
      const pdfGenerator = pdfMake.createPdf(docDefinition);
      const pdfBase64 = await getPdfBase64(pdfGenerator);
      const data = await enviarArquivo({
        to: celular,
        pdfBase64,
        paciente: {
          id: eco?.CODPAC,
          nome: eco?.NOMEPAC,
          medico: eco?.Realizou,
        },
        codLaudo: eco?.CODECO,
        fileName: `Laudo_Eco_${eco?.CODECO}.pdf`,
        caption: `Olá *${eco?.NOMEPAC}*, tudo bem? 😊

Segue em anexo o seu *Laudo de Ecocardiograma Doppler*.

As *imagens do exame* podem ser acessadas pelo link:
👉 http://www.icm.com.br (Resultados de Exames)

Qualquer dúvida, nossa equipe estará à disposição no setor de atendimento pelo WhatsApp 14-3402-5252\n
ICM - Instituto do Coração de Marília`,
        officialConfig: {
          procedimento: 'eco',
          documentTitle: 'Laudo de Ecocardiograma Doppler',
        },
      });
      toast.success(data.message);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.message ||
        'Erro ao enviar laudo via WhatsApp';
      toast.error(errorMessage);
    }
  } else {
    pdfMake.createPdf(docDefinition).download(`Laudo-${eco?.CODPAC}.pdf`);
  }

  return docDefinition;
}
